import { treaty } from '@elysiajs/eden';
import type { App } from '@metadevprofile/server/app';
import { createRefreshCoordinator, RefreshOutcome } from '@metadevprofile/shared';
import { authRoutes } from '@/auth/constants/routes';
import { env } from '@/common/constants/env';
import { resetFeatureStores } from '@/common/lib/reset-feature-stores';
import { tokenStorage } from '@/common/lib/token-storage';

// `POST /auth/refresh-token` is called with raw `fetch`, not through Eden Treaty, so the parsed
// body is the server payload itself — flat, exactly as `RefreshTokenResponse` declares it in
// @metadevprofile/shared. There is no `{ data }` envelope here: that wrapper is Eden's client-side
// return shape, not something the server emits.
type RefreshTokenBody = {
  access_token?: string;
  refresh_token?: string;
  expires_in?: number;
};

const isImpersonating = async (): Promise<boolean> => {
  const [{ store }, { selectImpersonationActive }] = await Promise.all([
    import('@/common/redux/store'),
    import('@/admin-user/stores/impersonation-slice'),
  ]);
  return selectImpersonationActive(store.getState());
};

const clearTokensAndResetStores = async (): Promise<void> => {
  await tokenStorage.clearAllTokens();
  await resetFeatureStores();

  const [{ store }, { resetAuth }] = await Promise.all([
    import('@/common/redux/store'),
    import('@/auth/stores/auth-slice'),
  ]);
  store.dispatch(resetAuth());

  window.location.href = authRoutes.signIn;
};

// Bumped on every successful refresh so concurrent callers can tell whether the token they
// observed is still current — see `createRefreshCoordinator`'s single-flight short-circuit.
let tokenGeneration = 0;
export const getCurrentTokenGeneration = (): number => tokenGeneration;

const performTokenRefresh = async (): Promise<RefreshOutcome> => {
  const refreshToken = await tokenStorage.getRefreshToken();
  if (!refreshToken) {
    await clearTokensAndResetStores();
    return RefreshOutcome.INVALID;
  }

  let response: Response;
  try {
    response = await fetch(`${env.SERVER_URL}/auth/refresh-token`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refresh_token: refreshToken }),
    });
  } catch {
    // Network-level failure (offline, DNS, CORS) — the session itself may still be valid,
    // so don't sign the user out over a transient connectivity blip.
    return RefreshOutcome.UNAVAILABLE;
  }

  if (!response.ok) {
    if (response.status >= 500) return RefreshOutcome.UNAVAILABLE;
    await clearTokensAndResetStores();
    return RefreshOutcome.INVALID;
  }

  // A 200 does not guarantee the expected payload — a gateway error page or an intercepting proxy
  // also answers 200. Validate the shape instead of indexing straight in, and fall back to the same
  // sign-out path as a failed response rather than throwing a TypeError.
  const tokens = (await response.json().catch(() => null)) as RefreshTokenBody | null;

  if (!tokens?.access_token) {
    await clearTokensAndResetStores();
    return RefreshOutcome.INVALID;
  }

  await tokenStorage.updateTokens(tokens.access_token, tokens.refresh_token, tokens.expires_in);
  tokenGeneration += 1;
  return RefreshOutcome.REFRESHED;
};

const TOKEN_REFRESH_LOCK_NAME = 'metadevprofile.token-refresh';

// navigator.locks dedups the actual refresh call across tabs (two tabs racing the same rotating
// refresh token would otherwise invalidate each other); createRefreshCoordinator dedups within a
// tab so every caller sharing a generation awaits the same in-flight promise instead of queueing
// on the lock one-by-one.
const performRefreshWithLock = (): Promise<RefreshOutcome> => {
  if (typeof navigator !== 'undefined' && navigator.locks?.request) {
    return navigator.locks.request(TOKEN_REFRESH_LOCK_NAME, () => performTokenRefresh());
  }
  return performTokenRefresh();
};

export const refreshCoordinator = createRefreshCoordinator({
  getTokenGeneration: getCurrentTokenGeneration,
  performRefresh: performRefreshWithLock,
});

export const httpClient = treaty<App>(env.SERVER_URL, {
  // Eden revives every ISO-8601 string into a Date by default; our shared types declare these
  // fields as `string`, and Dates are non-serializable in the RTK Query cache.
  parseDate: false,
  onRequest: async (_path, options) => {
    const token = await tokenStorage.getAccessToken();
    if (token) {
      const impersonating = await isImpersonating();
      const expiresAt = await tokenStorage.getExpiresAt();
      if (expiresAt && Date.now() > expiresAt && !impersonating) {
        const outcome = await refreshCoordinator(getCurrentTokenGeneration());

        if (outcome === RefreshOutcome.REFRESHED) {
          const newToken = await tokenStorage.getAccessToken();
          if (newToken) {
            return {
              headers: {
                ...options.headers,
                Authorization: `Bearer ${newToken}`,
              },
            };
          }
        }
        // INVALID already redirected to sign-in via clearTokensAndResetStores(). UNAVAILABLE
        // means a transient failure — fall through and send the stale token below; the request
        // 401s harmlessly while the redirect (if any) is in flight.
      }

      return {
        headers: {
          ...options.headers,
          Authorization: `Bearer ${token}`,
        },
      };
    }

    return options;
  },
  onResponse: async (response) => {
    if (!(await isImpersonating())) return;

    if (response.status === 401) {
      const { stopImpersonation } = await import('@/admin-user/stores/usecases/impersonation/stop-impersonation');
      await stopImpersonation();
      return;
    }
  },
});
